import { format } from "date-fns";

import { DateFormat } from "@/constants/DateFormat";
import { ExploreTestID, ScheduleTestID, TextTestID } from "@/constants/TestID";
import { IllustrationSize } from "@/constants/UI";
import EventDetailsAttendeeList from "@/ui/components/schedule/details/EventDetailsAttendeeList";
import EventDetailsLines from "@/ui/components/schedule/details/EventDetailsLines";
import EditEventButton from "@/ui/components/schedule/EditEventButton";
import type { AvatarCardProps } from "@/ui/core";
import { Text, TextTag } from "@/ui/core";
import { Box, VStack } from "@/ui/layout";
import { getIllustrationIcon } from "@/ui/theme/icons/illustrations/groupClasses";
import { LayoutToken } from "@/ui/theme/tokens";
import { getNextStartDate, getTimeRange } from "@/utils/date.util";
import { getRecurrenceString } from "@/utils/schedule.util";
import { joinTestIDs } from "@/utils/string.util";

interface EventDetailsProps {
  attendees: AvatarCardProps[];
  description?: string;
  endDate: string;
  externalId: string;
  frequency: EventRecurrence;
  groupClassExternalId?: string;
  groupSectionId?: string;
  iconName?: string;
  isGroupClassEvent?: boolean;
  isPhoneCallEvent?: boolean;
  isRecurring?: boolean;
  onEditEventPress?: () => void;
  patientExternalId: string;
  startDate: string;
  title: string;
  typeGroup: AppointmentTypeGroup;
}
type EventDetailsComponentProps = Omit<
  EventDetailsProps,
  | "groupClassExternalId"
  | "isPhoneCallEvent"
  | "patientExternalId"
  | "typeGroup"
>;

const baseTestID = ScheduleTestID.ScheduleEventDetails;

/* eslint complexity: ["error", {max: 7}] -- Feature flag increases complexity */
const EventDetails = ({
  attendees,
  description,
  endDate,
  externalId,
  frequency,
  iconName,
  isGroupClassEvent,
  isRecurring,
  onEditEventPress,
  startDate,
  title,
}: EventDetailsComponentProps) => {
  const recurrenceString = getRecurrenceString({
    frequency,
    startDate,
  });
  const nextStartDate = getNextStartDate({
    frequency,
    startDate,
  });
  const timeRange = getTimeRange({
    endDate,
    shouldIncludeStartAmPm: true,
    startDate,
  });
  const startsOn = format(new Date(startDate), DateFormat.weekDayShortMonthDay);

  return (
    <VStack gap={18} paddingTop={LayoutToken.ContentPaddingHorizontal}>
      {isGroupClassEvent && iconName && (
        <Box testID={joinTestIDs(baseTestID, ExploreTestID.Illustration)}>
          {getIllustrationIcon({
            height: IllustrationSize.Modal,
            iconName,
            width: IllustrationSize.Modal,
          })}
        </Box>
      )}
      <VStack gap={12}>
        <Text testID={joinTestIDs(baseTestID, TextTestID.Title)} variant="h4">
          {title}
        </Text>
        {/*
          Note: schedule events pre-emptively filter out sensitive events
          so no extra logic is needed here re: sensitive classes, and whether
          the app user is a patient or a proxy
        */}
        {isGroupClassEvent && <TextTag text="Registered" />}
      </VStack>
      <VStack gap={4}>
        <EventDetailsLines
          isRecurring={isRecurring}
          nextStartDate={nextStartDate}
          recurrenceString={recurrenceString}
          startDate={startsOn}
          testID={baseTestID}
          timeRange={timeRange}
        />
        <EditEventButton
          eventId={externalId}
          isGroupClassEvent={isGroupClassEvent || false}
          isRecurring={isRecurring}
          onPress={() => onEditEventPress?.()}
        />
        <EventDetailsAttendeeList
          attendees={attendees}
          isGroupClassEvent={Boolean(isGroupClassEvent)}
        />
        {isGroupClassEvent && (
          <Text
            testID={joinTestIDs(baseTestID, TextTestID.Description)}
            variant="body"
          >
            {description}
          </Text>
        )}
      </VStack>
    </VStack>
  );
};

export type { EventDetailsProps };
export default EventDetails;
